/** The road to the race: phases as one line from plan start to race day, with today marked.
 * The single signature element of the design (docs/design.md). */
import type { Phase } from "@/api/schemas";
import { dayYear, num } from "@/lib/format";
import { mondayOf, phaseColor } from "@/lib/plan";

export function RaceLine({
  phases,
  today,
  goalName,
  goalDate,
  placeholder = false,
  onPick,
}: {
  phases: Phase[];
  today: string;
  goalName?: string;
  goalDate?: string;
  /** "No race yet": no date at the end, the line ends where the phases end */
  placeholder?: boolean;
  onPick?: (phaseStart: string) => void;
}) {
  if (phases.length === 0) return null;
  const t0 = Date.parse(phases[0]!.start);
  const t1 = Date.parse((placeholder ? undefined : goalDate) ?? phases[phases.length - 1]!.end);
  const span = Math.max(1, t1 - t0);
  const nowPct = Math.max(0, Math.min(100, ((Date.parse(today) - t0) / span) * 100));
  const current = phases.find((p) => p.start <= today && today <= p.end);
  return (
    <div className="raceline" data-testid="raceline">
      <div
        className="raceline-track"
        role="group"
        aria-label={placeholder ? "Training phases" : "Training phases to the race"}
      >
        {phases.map((p) => (
          <button
            key={p.name + p.start}
            type="button"
            // one tab stop for the page, not one per phase: the phases are also listed on the Plan page
            tabIndex={-1}
            className={p === current ? "now" : ""}
            title={`${p.name}: ${dayYear(p.start)} – ${dayYear(p.end)}, ${num(p.hours_from, 1)}–${num(p.hours_to, 1)} h a week. ${p.focus}`}
            aria-label={`${p.name}, ${dayYear(p.start)} to ${dayYear(p.end)}`}
            style={{
              width: `${((Date.parse(p.end) - Date.parse(p.start)) / span) * 100}%`,
              background: phaseColor(p.name),
            }}
            onClick={() => onPick?.(mondayOf(p.start))}
          />
        ))}
        <span className="raceline-now" style={{ left: `${nowPct}%` }} aria-hidden />
      </div>
      <div className="raceline-ends">
        <span>{current ? current.name : "Plan start"}</span>
        <span>
          {placeholder
            ? "No race yet"
            : goalName
              ? `${goalName}, ${dayYear(goalDate ?? phases[phases.length - 1]!.end)}`
              : ""}
        </span>
      </div>
    </div>
  );
}
